import { Pencil, Check, LogOut, Palette, AlertTriangle } from 'lucide-react';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { logoutSession } from '../../lib/api.js';

/**
 * EditToolbar — schwebende Bearbeiten-Leiste für eingeloggte tenant_admins
 * (Issue #3, Storefront-First-Editing). Nur sichtbar, wenn die Session die
 * Rolle tenant_admin für diesen Mandanten hat (canEdit).
 * „Abmelden" löscht die Session-Cookies und lädt die Storefront anonym neu.
 */
export function EditToolbar() {
  const { canEdit, editMode, toggleEditMode, exitEditMode, designOpen, toggleDesign, sessionExpired } = useEditMode();
  if (!canEdit) return null;

  const handleLogout = async () => {
    try {
      await logoutSession();
    } catch {
      /* Session ist danach in jedem Fall unbrauchbar */
    }
    window.location.reload();
  };

  return (
    <div
      data-testid="edit-toolbar"
      className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 flex items-center gap-3 rounded-full border border-[var(--border)] bg-white/95 px-4 py-2 shadow-2xl backdrop-blur"
    >
      {sessionExpired ? (
        <span
          data-testid="session-expired-warning"
          className="flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-300"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Sitzung abgelaufen. Bitte neu anmelden.
        </span>
      ) : editMode ? (
        <>
          <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-700">
            <Check className="h-4 w-4" /> Bearbeiten aktiv
          </span>
          <span className="hidden sm:inline text-xs text-slate-500">
            Klicken Sie auf einen markierten Text, um ihn zu ändern. Leer = Standardtext.
          </span>
          <button
            type="button"
            onClick={exitEditMode}
            data-testid="edit-toolbar-done"
            className="rounded-full bg-[var(--primary)] px-3 py-1.5 text-xs font-bold text-[var(--primary-foreground,#ffffff)] cursor-pointer hover:opacity-90"
          >
            Fertig
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={toggleEditMode}
            data-testid="edit-toolbar-start"
            className="flex items-center gap-1.5 rounded-full bg-[var(--primary)] px-3 py-1.5 text-xs font-bold text-[var(--primary-foreground,#ffffff)] cursor-pointer hover:opacity-90"
          >
            <Pencil className="h-3.5 w-3.5" /> Shop bearbeiten
          </button>
          <span className="hidden sm:inline text-xs text-slate-500">
            Sie sind als Shop-Administrator angemeldet.
          </span>
        </>
      )}
      <button
        type="button"
        onClick={toggleDesign}
        data-testid="edit-toolbar-design"
        aria-pressed={designOpen}
        aria-controls="design-drawer"
        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold cursor-pointer ${
          designOpen
            ? 'border-slate-900 bg-slate-900 text-white'
            : 'border-[var(--border)] text-slate-700 hover:bg-slate-100'
        }`}
      >
        <Palette className="h-3.5 w-3.5" />
        Design
      </button>
      <button
        type="button"
        onClick={handleLogout}
        data-testid="edit-toolbar-logout"
        title="Abmelden"
        aria-label="Abmelden"
        className="flex items-center gap-1 rounded-full border border-[var(--border)] px-2.5 py-1.5 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
      >
        <LogOut className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Abmelden</span>
      </button>
    </div>
  );
}